import React, {
  ReactNode,
  useMemo,
  useState,
} from 'react';
import {
  CheckEligibilityResponseDTO,
  PatientDTO,
} from 'dtos';
import eligibilityStateConfig from 'views/EMR/components/Eligibility/eligibilityStateConfig';
import Dialog from 'pages/Dashboard/components/Dialog';
import PatientInfo from 'pages/Dashboard/components/PatientInfo';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import EligibilityInformation from 'views/EMR/components/Eligibility/EligibilityInformation';
import EligibilityEffective from 'views/EMR/components/Eligibility/EligibilityEffective';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import EligibilityOverview from 'views/EMR/components/Eligibility/Overview';
import Tag from 'core/components/Tag';
import isNil from 'lodash/isNil';

const FAKE_SEARCH_IDS = [
  '00000000-0000-0000-0000-000000000000',
  '11111111-1111-1111-1111-111111111111',
  '22222222-2222-2222-2222-222222222222',
];

export type EligibilityInformationDialogProps = CheckEligibilityResponseDTO & {
  patient?: PatientDTO;
  insuranceName?: string | null;
};

type Props = EligibilityInformationDialogProps & {
  onClose: () => void;
};

type TabProps = {
  key: string;
  label: string;
  content: ReactNode;
  shouldHide?: boolean;
};

const getTabs = ({ benefits, overview }: Partial<CheckEligibilityResponseDTO>) => {
  const tabs: TabProps[] = [
    {
      key: 'benefits',
      label: 'Benefits',
      content: <EligibilityInformation
        columnHeaders={benefits?.columnHeaders ?? []}
        serviceTypeBreakdowns={benefits?.serviceTypeBreakdowns ?? []}
      />,
      shouldHide: isNil(benefits?.serviceTypeBreakdowns)
        || benefits?.serviceTypeBreakdowns.length === 0,
    },
    {
      key: 'overview',
      label: 'Overview',
      content: <EligibilityOverview overview={overview} />,
    },
  ].filter(({ shouldHide }) => !shouldHide);

  return tabs;
};

export default function EligibilityInformationDialog({
  onClose,
  patient,
  insuranceName,
  overview,
  benefits,
  effectiveFrom,
  effectiveTill,
  eligibilityState,
  eligibilitySearchId,
}: Props) {
  const [activeTab, setActiveTab] = useState(0);
  const stateConfig = !isNil(eligibilityState?.state)
    ? eligibilityStateConfig[eligibilityState?.state!]
    : null;
  const tabs = useMemo(
    () => getTabs({ benefits, overview }),
    [benefits, overview],
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={(
        <div className="flex gap-1 flex-col">
          <PatientInfo
            patient={(patient ?? {}) as Patient}
            isLink={false}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium text-gray-600">
              Insurance Eligibility Information for
              {' '}
              {insuranceName}
            </div>
            {FAKE_SEARCH_IDS.includes(eligibilitySearchId ?? '') && (
              <Tag defaultColor="bg-amber-200 text-amber-800" className="!font-semibold">
                Fake
              </Tag>
            )}
          </div>
          {!isNil(stateConfig) && (
            <div className="flex items-center gap-1.5 text-sm text-gray-700">
              <stateConfig.Icon className={stateConfig.className} />
              {!isNil(eligibilityState?.description) && (
                <span>{eligibilityState?.description}</span>
              )}
            </div>
          )}
          <div className="text-sm text-gray-500">
            <EligibilityEffective
              effectiveFrom={effectiveFrom}
              effectiveTill={effectiveTill}
              classes={{
                root: 'text-[13px] text-gray-600',
                label: 'font-medium text-gray-500',
                date: 'font-semibold text-gray-900 text-[14px] tracking-tight',
              }}
            />
          </div>
        </div>
      )}
      classes={{
        root: '!w-full !h-screen flex flex-col !p-0 !space-y-0 !overflow-hidden',
        dialog: 'hidden-backdrop populate-iframe-modal !m-0 settings-form-dialog',
        bodyRoot: '!w-screen !m-0 !max-w-[calc(100%-12px)]',
        container: '!justify-start',
        title: 'border-b border-b-gray-300 p-4',
      }}
      maxWidth={false}
      fullWidth
    >
      <div className="w-full min-h-0 flex-1 flex flex-col overflow-hidden">
        <div className="px-4 pt-4">
          <NavigationTabs
            className="!min-h-[initial]"
            classes={{
              tab: '!font-semibold hover:!text-blue-500',
              active: '!text-blue-500 !border-blue-500',
            }}
            tabs={tabs}
            activeIndex={activeTab}
            onClick={setActiveTab}
          />
        </div>
        {activeTab < tabs.length && tabs[activeTab].content}
      </div>
    </Dialog>
  );
}
